import Splide from "@splidejs/splide";
import "@splidejs/splide/css";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { initKvLoading, markKvIntroPlayed, shouldSkipKvOpening, syncWideHexVisibility } from "./kv-loading.js";
import { initKvDebug, kvDebugMark } from "./kv-debug.js";
import { initPageFrame, paintPageFrame } from "./page-frame.js";

initKvDebug();

const lenis = new Lenis({
  autoRaf: true,
  lerp: 0.1,
  anchors: true,
});

const easeOutPagetop = (t) => 1 - (1 - t) ** 4;

let scrollLocks = 0;

const preventNativeScroll = (event) => {
  const target = event.target;
  if (
    target instanceof Element &&
    target.closest("[data-lenis-prevent], .drawer__panel, .modal__dialog, .modal__shell")
  ) {
    return;
  }
  event.preventDefault();
};

const lockPageScroll = () => {
  if (scrollLocks === 0) {
    document.documentElement.classList.add("is-locked");
    document.body.classList.add("is-locked");
    lenis.stop();
    // overflow:hidden / スクロールバー隙間埋めはしない（sticky ずれ・額付き防止）
    window.addEventListener("wheel", preventNativeScroll, { passive: false });
    window.addEventListener("touchmove", preventNativeScroll, { passive: false });
  }
  scrollLocks += 1;
};

const unlockPageScroll = () => {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks > 0) return;
  document.documentElement.classList.remove("is-locked");
  document.body.classList.remove("is-locked");
  window.removeEventListener("wheel", preventNativeScroll);
  window.removeEventListener("touchmove", preventNativeScroll);
  lenis.start();
};

const kvRoot = document.querySelector(".js-kv");
const SCROLL_STORAGE_KEY = `inoac:scroll:${location.pathname}`;
const savedScrollY = (() => {
  try {
    return Number(sessionStorage.getItem(SCROLL_STORAGE_KEY) || 0);
  } catch {
    return 0;
  }
})();

const persistScrollPosition = () => {
  try {
    sessionStorage.setItem(
      SCROLL_STORAGE_KEY,
      String(Math.round(window.scrollY || 0)),
    );
  } catch {
    /* ignore quota / private mode */
  }
};

window.addEventListener("pagehide", persistScrollPosition);

// SP: UI バー出し入れに合わせてフレーム・fixed UI を visualViewport に追従（KV 高さ --kv-vh は別途ロック）
const syncSpVisualViewport = () => {
  const root = document.documentElement;
  if (!window.matchMedia("(max-width: 768px)").matches) {
    for (const key of [
      "--sp-vv-h",
      "--sp-vv-w",
      "--sp-vv-offset-y",
      "--sp-vv-offset-x",
      "--sp-vv-bottom-gap",
    ]) {
      root.style.removeProperty(key);
    }
    return;
  }

  const vv = window.visualViewport;
  const h = Math.round(vv?.height ?? window.innerHeight);
  const w = Math.round(vv?.width ?? window.innerWidth);
  const oy = Math.round(vv?.offsetTop ?? 0);
  const ox = Math.round(vv?.offsetLeft ?? 0);
  const layoutH = window.innerHeight;
  // head の --kv-vh 先行ロックより低い vv だけを信じない（フレーム下端の欠け対策）
  const kvVh = Math.round(
    Number.parseFloat(root.style.getPropertyValue("--kv-vh")) || 0,
  );
  const frameH = Math.max(h, kvVh);

  root.style.setProperty("--sp-vv-h", `${frameH}px`);
  root.style.setProperty("--sp-vv-w", `${w}px`);
  root.style.setProperty("--sp-vv-offset-y", `${oy}px`);
  root.style.setProperty("--sp-vv-offset-x", `${ox}px`);
  root.style.setProperty(
    "--sp-vv-bottom-gap",
    `${Math.max(0, layoutH - oy - frameH)}px`,
  );
  paintPageFrame();
};

initPageFrame();
syncSpVisualViewport();
window.addEventListener("resize", syncSpVisualViewport, { passive: true });
window.visualViewport?.addEventListener("resize", syncSpVisualViewport, {
  passive: true,
});
window.visualViewport?.addEventListener("scroll", syncSpVisualViewport, {
  passive: true,
});

let softEnterReleased = false;

const releasePageEnter = () => {
  document.body.classList.remove("is-page-transitioning");

  const veil = document.querySelector(".page-transition");
  const softEnter = document.documentElement.classList.contains(
    "is-page-enter-soft",
  );
  const pageEnter = document.body.classList.contains("is-page-enter");

  if (softEnter && veil && !softEnterReleased) {
    softEnterReleased = true;
    // クラス依存だと transition が噛み合わず opacity が 1 のまま残ることがあるため
    // インラインで明示的に 1 → 0 する
    veil.style.opacity = "1";
    document.documentElement.classList.remove("is-page-enter-soft");
    const fadeOut = () => {
      veil.style.opacity = "0";
      window.setTimeout(() => {
        veil.style.removeProperty("opacity");
      }, 420);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(fadeOut);
    });
    window.setTimeout(fadeOut, 100);
  }

  if (!pageEnter) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.body.classList.remove("is-page-enter");
    });
  });
  window.setTimeout(() => {
    document.body.classList.remove("is-page-enter");
  }, 80);
};

window.addEventListener("pageshow", releasePageEnter);
if (document.readyState === "complete") {
  releasePageEnter();
} else {
  window.addEventListener("load", releasePageEnter, { once: true });
}

const restoreSavedScroll = () => {
  if (savedScrollY <= 24) return;
  // トップはセッション内再訪問を MV 先頭から
  if (document.body.classList.contains("page--top")) return;
  // introPlayed では復元しない（History 等が毎回末尾付近に戻るのを防ぐ）
  // reload / 戻る・進むのときだけ復元
  const nav = performance.getEntriesByType?.("navigation")?.[0];
  const type = nav?.type || "";
  if (type !== "reload" && type !== "back_forward") return;
  window.scrollTo(0, savedScrollY);
  lenis.scrollTo(savedScrollY, { immediate: true });
};

let kvMotionReady = !kvRoot || kvRoot.dataset.kvOpening !== "true";
const skipKvOpening = shouldSkipKvOpening();
/** KV 表示をスクロール位置に同期（アンカー / pagetop / scrollend から呼ぶ） */
let syncKv = () => {};
const syncKvAfterSettle = () => {
  syncKv();
  requestAnimationFrame(() => {
    syncKv();
    requestAnimationFrame(syncKv);
  });
};

if (kvRoot?.dataset.kvOpening === "true" && !skipKvOpening) {
  lockPageScroll();
}

if (kvRoot) {
  const scrollEl = kvRoot.querySelector(".kv__pin");
  const stickyEl = kvRoot.querySelector(".kv__sticky");
  const copyEl = kvRoot.querySelector(".kv__copy");
  const bgEl = kvRoot.querySelector(".kv__bg");
  const fieldEl = kvRoot.querySelector(".kv__field");
  const messageEl = kvRoot.querySelector(".kv__message");
  const messageLines = [...kvRoot.querySelectorAll(".kv__message-line")];
  const bottomEl = kvRoot.querySelector(".kv__bottom");
  const tickerEl = kvRoot.querySelector(".kv__ticker");
  const scrollCueEl = kvRoot.querySelector(".kv__scroll");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const range = (progress, start, end) => {
    if (end <= start) return progress >= end ? 1 : 0;
    return clamp((progress - start) / (end - start), 0, 1);
  };

  const BG_OPACITY_MIN = 0.2;
  const LINE_OPACITY_MID = 0.18;
  const COPY_FADE_START = 0.18;
  const COPY_FADE_END = 0.46;
  // SP: SCROLL→NEWS（COPY_FADE_START）→ NEWS フェードアウト → その後メッセージ
  const NEWS_FADE_START = COPY_FADE_END;
  const NEWS_FADE_END = 0.54;
  const MESSAGE_GAP_AFTER_NEWS = 0.01;
  const MESSAGE_START_SP = NEWS_FADE_END + MESSAGE_GAP_AFTER_NEWS;
  const MESSAGE_END_SP = 1;
  const MESSAGE_START = 0.44;
  const MESSAGE_END = 0.92;
  // SP: SCROLL 消灯 → NEWS 点灯（重ねない）。pin 進行度の割合
  const SCROLL_OUT_START = COPY_FADE_START;
  const SCROLL_OUT_END = 0.25;
  const NEWS_IN_START = SCROLL_OUT_END;
  const NEWS_IN_END = 0.32;

  const smoothStep = (t) => t * t * (3 - 2 * t);

  /** SP: copy はフェード。SCROLL→NEWS は順次フェード（非クロス）。NEWS は MESSAGE より先に完全消灯 */
  const getSpScrollNewsOpacity = (progress) => {
    const tickerReady = Boolean(tickerEl?.classList.contains("is-shown"));

    if (progress < SCROLL_OUT_START) {
      return { scroll: 1, ticker: 0, revealTicker: false };
    }

    let scroll = 0;
    let tickerBase = 0;

    if (reducedMotion) {
      scroll = 0;
      tickerBase = 1;
    } else if (progress < SCROLL_OUT_END) {
      scroll = 1 - smoothStep(range(progress, SCROLL_OUT_START, SCROLL_OUT_END));
      tickerBase = 0;
    } else if (progress < NEWS_IN_END) {
      scroll = 0;
      tickerBase = smoothStep(range(progress, NEWS_IN_START, NEWS_IN_END));
    } else {
      scroll = 0;
      tickerBase = 1;
    }

    let newsFade = 1;
    if (progress >= NEWS_FADE_END) {
      newsFade = 0;
    } else if (progress >= NEWS_FADE_START) {
      newsFade = 1 - smoothStep(range(progress, NEWS_FADE_START, NEWS_FADE_END));
    }

    const ticker = tickerReady ? tickerBase * newsFade : 0;
    return {
      scroll,
      ticker,
      revealTicker: tickerReady && ticker > 0.02,
    };
  };

  const applySpBottomChrome = (progress) => {
    const { scroll, ticker, revealTicker } = getSpScrollNewsOpacity(progress);
    lastScrollCue = writeOpacity(scrollCueEl, scroll, lastScrollCue);
    lastTickerOp = writeOpacity(tickerEl, ticker, lastTickerOp);
    if (scrollCueEl) {
      scrollCueEl.style.pointerEvents = scroll > 0.05 ? "" : "none";
    }
    if (tickerEl) {
      tickerEl.classList.toggle("is-kv-ticker-revealed", revealTicker);
      tickerEl.style.pointerEvents = ticker > 0.05 ? "" : "none";
    }
    if (bottomEl) {
      const pe = Math.max(scroll, ticker);
      bottomEl.style.pointerEvents = pe > 0.05 ? "" : "none";
    }
  };

  let viewH = 0;
  let largeH = 0;
  let pinH = 0;
  let messageH = 0;
  let lineHs = [];
  let measuredWidth = -1;
  let lastCopy = NaN;
  let lastScrollCue = NaN;
  let lastTickerOp = NaN;
  let lastBottom = NaN;
  let lastBg = NaN;
  let lastOffset = NaN;
  let lastMessageOp = NaN;
  let lastTickerHidden = null;
  let lastBottomInset = null;
  const lastLineOps = messageLines.map(() => NaN);
  let hexEls = [];
  let lastHexOps = [];
  let lastField = NaN;
  let linesDone = false;

  const usesKvField = () =>
    Boolean(fieldEl) && !kvRoot.classList.contains("is-kv-fallback");

  const syncHexEls = () => {
    if (!usesKvField()) {
      hexEls = [];
      lastHexOps = [];
      return;
    }
    hexEls = [...kvRoot.querySelectorAll(".kv-hex")];
    if (lastHexOps.length !== hexEls.length) {
      lastHexOps = hexEls.map(() => NaN);
    }
  };

  const getRegionOpacity = (topY, progress, opaqueY, fadeStartY, appearStartY) => {
    if (progress <= 0.02) return 1;
    if (topY <= opaqueY) return 1;
    if (topY <= fadeStartY) {
      const t =
        fadeStartY === opaqueY
          ? 1
          : clamp((fadeStartY - topY) / (fadeStartY - opaqueY), 0, 1);
      return LINE_OPACITY_MID + (1 - LINE_OPACITY_MID) * t;
    }
    if (topY < appearStartY) {
      return (
        LINE_OPACITY_MID *
        clamp((appearStartY - topY) / (appearStartY - fadeStartY), 0, 1)
      );
    }
    return 0;
  };

  const measureViewportUnit = (unit) => {
    const probe = document.createElement("div");
    probe.style.cssText =
      `position:fixed;top:0;height:100${unit};pointer-events:none;visibility:hidden`;
    document.body.append(probe);
    const h = probe.offsetHeight;
    probe.remove();
    return h || window.innerHeight;
  };

  const measureKv = () => {
    pinH = scrollEl?.offsetHeight || 0;
    messageH = messageEl?.offsetHeight || 0;
    lineHs = messageLines.map((line) => line.offsetHeight);
    measuredWidth = window.innerWidth;
    viewH = measureViewportUnit("svh");
    largeH = measureViewportUnit("lvh");
  };

  const isKvSp = () => window.matchMedia("(max-width: 768px)").matches;

  // SP: --kv-vh は「妥当な 100svh」で一度だけ確定（UI バー出し入れの dvh で伸びない）
  // Chrome iOS は boot 時に svh/vv とも 501 等の過小値を返すことがある → 妥当になるまで再計測し、
  // 生の large vv（ツールバー退避後）は追わない
  const KV_VH_MIN_RATIO = 0.65;
  const KV_VH_LARGE_SLACK_PX = 32;
  const KV_VH_SVH_FALLBACK_RATIO = 0.9;

  const prelockW = Number(document.documentElement.dataset.kvSpPrelockW) || 0;
  const prelockH = Number(document.documentElement.dataset.kvSpPrelockH) || 0;
  let kvSpLayout = {
    w: prelockW,
    h: prelockH,
    locked: Boolean(
      isKvSp() &&
        document.documentElement.classList.contains("is-kv-sp-prelock") &&
        prelockH > 0,
    ),
    settled: false,
    source: prelockH > 0 ? "prelock" : "",
  };

  const clearKvSpVhDataset = () => {
    delete document.documentElement.dataset.kvSpPrelockW;
    delete document.documentElement.dataset.kvSpPrelockH;
    delete document.documentElement.dataset.kvSpVhSettled;
    delete document.documentElement.dataset.kvSpVhSource;
  };

  const resetKvSpLayoutIfRotated = () => {
    if (!kvSpLayout.locked && !kvSpLayout.settled) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const wasPortrait = kvSpLayout.w < kvSpLayout.h;
    if ((w < h) !== wasPortrait) {
      kvSpLayout = { w: 0, h: 0, locked: false, settled: false, source: "" };
      document.documentElement.style.removeProperty("--kv-vh");
      document.documentElement.classList.remove("is-kv-sp-prelock");
      clearKvSpVhDataset();
    }
  };

  const applyKvSpVhLock = (w, h, settled, source) => {
    document.documentElement.style.setProperty("--kv-vh", `${h}px`);
    document.documentElement.dataset.kvSpPrelockW = String(w);
    document.documentElement.dataset.kvSpPrelockH = String(h);
    document.documentElement.dataset.kvSpVhSettled = settled ? "1" : "0";
    document.documentElement.dataset.kvSpVhSource = source;
    kvSpLayout = { w, h, locked: true, settled, source };
  };

  const resolveKvSpVh = () => {
    const svh = Math.round(measureViewportUnit("svh"));
    const lvh = Math.round(measureViewportUnit("lvh"));
    const vv = Math.round(window.visualViewport?.height ?? window.innerHeight);
    const clientH = Math.round(document.documentElement.clientHeight || 0);
    const refH = Math.max(
      window.screen?.height || 0,
      window.outerHeight || 0,
      lvh,
      vv,
      svh,
    );

    const isBogus = (h) => h <= 0 || (refH > 0 && h < refH * KV_VH_MIN_RATIO);
    const isLargeLike = (h) => lvh > 0 && h >= lvh - KV_VH_LARGE_SLACK_PX;

    if (!isBogus(svh)) {
      return { h: svh, settle: true, source: "svh" };
    }

    const mid = [clientH, vv]
      .filter((h) => !isBogus(h) && !isLargeLike(h))
      .sort((a, b) => b - a);
    if (mid[0]) {
      return { h: mid[0], settle: true, source: "mid" };
    }

    // svh が壊れたまま・見えるのは large vv だけ → dvh 相当は追わず svh 推定
    const base = Math.max(lvh, vv);
    if (!isBogus(base)) {
      const estimated = Math.round(base * KV_VH_SVH_FALLBACK_RATIO);
      if (!isBogus(estimated)) {
        return { h: estimated, settle: true, source: "estimate" };
      }
    }

    return {
      h: Math.max(svh, clientH, 0),
      settle: false,
      source: "provisional",
    };
  };

  const lockKvSpLayout = () => {
    if (!isKvSp() || !stickyEl) return;
    if (stickyEl.getBoundingClientRect().top < -8) return;

    stickyEl.style.setProperty("--kv-bottom-inset", "12px");

    if (kvSpLayout.settled) return;

    const w = Math.round(window.visualViewport?.width ?? window.innerWidth);
    const resolved = resolveKvSpVh();
    if (resolved.h <= 0) return;

    // 上方向のみ（prelock の過小値からの補正）。縮小はしない
    if (kvSpLayout.locked && resolved.h <= kvSpLayout.h) {
      if (resolved.settle && !kvSpLayout.settled) {
        applyKvSpVhLock(w, kvSpLayout.h, true, resolved.source);
      }
      return;
    }

    applyKvSpVhLock(w, resolved.h, resolved.settle, resolved.source);
  };

  const syncKvVisual = () => {
    if (!stickyEl) return;
    if (!isKvSp()) {
      kvSpLayout = { w: 0, h: 0, locked: false, settled: false, source: "" };
      document.documentElement.style.removeProperty("--kv-vh");
      document.documentElement.classList.remove("is-kv-sp-prelock");
      clearKvSpVhDataset();
      return;
    }
    resetKvSpLayoutIfRotated();
    lockKvSpLayout();
  };

  const syncKvTicker = () => {
    if (!tickerEl) return;
    if (!isKvSp()) {
      tickerEl.classList.remove("is-shown");
      lastTickerHidden = false;
      return;
    }
    if (!viewH) measureKv();
    const vvH = window.visualViewport?.height ?? window.innerHeight;
    const hidden = vvH <= viewH + 24;
    if (hidden === lastTickerHidden) return;
    lastTickerHidden = hidden;
    tickerEl.classList.toggle("is-shown", !hidden);
  };

  const syncKvBottom = () => {
    if (!stickyEl) return;
    if (!isKvSp()) {
      stickyEl.style.removeProperty("--kv-bottom-inset");
      lastBottomInset = null;
      return;
    }
    resetKvSpLayoutIfRotated();
    lockKvSpLayout();
  };

  const syncKvChrome = () => {
    // 先に妥当な --kv-vh を確定（または暫定更新）してから --sp-vv-* / フレームを追従
    syncKvVisual();
    syncSpVisualViewport();
    syncKvTicker();
    syncKvBottom();
  };

  const getProgress = () => {
    if (!scrollEl) return 0;
    const scrollable = pinH - viewH;
    if (scrollable <= 0) return 0;
    return clamp(-scrollEl.getBoundingClientRect().top / scrollable, 0, 1);
  };

  const getMessageOffset = (progress, start, end) => {
    const startOffset = viewH / 2 + messageH / 2;
    const shift = range(progress, start, end);
    return startOffset * (1 - shift);
  };

  const getLeadExit = (messageOffset) => {
    const centerY = viewH / 2 + messageOffset;
    const start = viewH / 2;
    const end = -messageH / 2;
    if (start <= end) return centerY <= end ? 1 : 0;
    return clamp((start - centerY) / (start - end), 0, 1);
  };

  const getKvExitMul = () => {
    if (!stickyEl) return 1;
    const top = stickyEl.getBoundingClientRect().top;
    const t = clamp(-top / (viewH * 0.8), 0, 1);
    return 1 - t * t * (3 - 2 * t);
  };

  // sticky が実質ピンとして残っているか（下端数 px だけ見える状態は pastPin）
  const isKvStickyInView = () => {
    if (!stickyEl) return true;
    if (!viewH) return true;
    const rect = stickyEl.getBoundingClientRect();
    const visible = Math.min(rect.bottom, viewH) - Math.max(rect.top, 0);
    return visible > viewH * 0.2;
  };

  /** @returns {"inPin" | "pastPin"} */
  const resolveKvMode = () => {
    // #products 以降 / sticky 画面外 → pastPin（UI 一式オフ）
    if (!isKvStickyInView()) return "pastPin";
    return "inPin";
  };

  const applyKvPastPin = () => {
    const kvExit = getKvExitMul();
    const bgFade = Math.max(BG_OPACITY_MIN * kvExit, getNewsBgFade());
    writeKvBackground(bgFade);
    syncKvHexOpacity({
      progress: 1,
      travel: 1,
      bgFade,
      opaqueY: 0,
      fadeStartY: 0,
      appearStartY: 0,
      uniformOnly: true,
    });

    lastCopy = writeOpacity(copyEl, 0, lastCopy);
    lastScrollCue = writeOpacity(scrollCueEl, 0, lastScrollCue);
    lastTickerOp = writeOpacity(tickerEl, 0, lastTickerOp);
    tickerEl?.classList.remove("is-kv-ticker-revealed");
    lastMessageOp = writeOpacity(messageEl, 0, lastMessageOp);
    // SP は bottom コンテナ自体は残し、SCROLL / NEWS だけ 0（従来 early return と同じ）
    if (isKvSp()) {
      lastBottom = writeOpacity(bottomEl, 1, lastBottom);
    } else {
      lastBottom = writeOpacity(bottomEl, 0, lastBottom);
    }
    if (copyEl) copyEl.style.pointerEvents = "none";
    if (bottomEl) bottomEl.style.pointerEvents = "none";
    if (messageEl && lastOffset !== 0) {
      messageEl.style.transform = "translate(-50%, calc(-50% + 0px))";
      lastOffset = 0;
    }
  };

  const getSpMessageOpacity = (progress, travel) => {
    if (!isKvStickyInView()) return 0;
    const kvExit = getKvExitMul();
    if (kvExit <= 0.001) return 0;
    const base = reducedMotion
      ? progress >= 0.5
        ? 1
        : 0
      : Math.round(travel * 1000) / 1000;
    return base * kvExit;
  };

  const getNewsBgFade = () => 0;

  const writeOpacity = (el, value, prev) => {
    if (!el || prev === value) return value;
    el.style.opacity = String(value);
    return value;
  };

  const writeKvBackground = (bgFade) => {
    if (usesKvField()) {
      lastField = writeOpacity(fieldEl, bgFade, lastField);
      if (bgEl) bgEl.style.opacity = "0";
      return;
    }
    lastBg = writeOpacity(bgEl, bgFade, lastBg);
  };

  const syncKvHexOpacity = ({
    progress,
    travel,
    bgFade,
    opaqueY,
    fadeStartY,
    appearStartY,
    uniformOnly,
  }) => {
    if (!usesKvField() || !hexEls.length) return;

    hexEls.forEach((hex, i) => {
      const shell = hex.querySelector(".kv-hex__shell");
      let hexOp = bgFade;
      if (!uniformOnly && travel > 0 && travel < 1) {
        const centerY = hex.getBoundingClientRect().top + hex.offsetHeight / 2;
        hexOp =
          bgFade *
          getRegionOpacity(
            centerY,
            progress,
            opaqueY,
            fadeStartY,
            appearStartY,
          );
        lastHexOps[i] = writeOpacity(shell, hexOp, lastHexOps[i]);
        return;
      }

      if (shell?.style.opacity) shell.style.removeProperty("opacity");
      lastHexOps[i] = NaN;
    });
  };

  const updateKv = () => {
    if (!kvMotionReady) return;

    if (measuredWidth !== window.innerWidth || !viewH) measureKv();
    syncHexEls();

    const progress = getProgress();
    const mode = resolveKvMode();
    kvRoot.dataset.kvMode = mode;

    // pastPin: pin 外 / 下セクション表示中は COPY・SCROLL・NEWS・message をオフ
    if (mode === "pastPin") {
      applyKvPastPin();
      return;
    }

    // inPin: pin 終端の最適化パス（sticky はまだ実質残っている）
    if (progress >= 1 && linesDone) {
      const kvExit = getKvExitMul();
      const kvLeft = BG_OPACITY_MIN * kvExit;
      const bgFade = Math.max(kvLeft, getNewsBgFade());
      writeKvBackground(bgFade);
      syncKvHexOpacity({
        progress,
        travel: 1,
        bgFade,
        opaqueY: 0,
        fadeStartY: 0,
        appearStartY: 0,
        uniformOnly: true,
      });
      const spDone = isKvSp();
      lastCopy = writeOpacity(copyEl, 0, lastCopy);
      if (spDone) {
        lastBottom = writeOpacity(bottomEl, 1, lastBottom);
        lastScrollCue = writeOpacity(scrollCueEl, 0, lastScrollCue);
        lastTickerOp = writeOpacity(tickerEl, 0, lastTickerOp);
        tickerEl?.classList.remove("is-kv-ticker-revealed");
        lastMessageOp = writeOpacity(
          messageEl,
          getSpMessageOpacity(1, 1),
          lastMessageOp,
        );
      } else {
        lastScrollCue = writeOpacity(scrollCueEl, 0, lastScrollCue);
        lastBottom = writeOpacity(bottomEl, 0, lastBottom);
        lastTickerOp = writeOpacity(tickerEl, 0, lastTickerOp);
        // pin 終端でも sticky が上に抜けるときは kvExit でフェード（pastPin で突然 0 にしない）
        lastMessageOp = writeOpacity(messageEl, getKvExitMul(), lastMessageOp);
      }
      if (copyEl) copyEl.style.pointerEvents = "none";
      if (bottomEl) bottomEl.style.pointerEvents = "none";
      if (messageEl) {
        const settledOffset = 0;
        if (settledOffset !== lastOffset) {
          messageEl.style.transform = `translate(-50%, calc(-50% + ${settledOffset}px))`;
          lastOffset = settledOffset;
        }
      }
      return;
    }

    const sp = isKvSp();
    const copyFade = 1 - range(progress, COPY_FADE_START, COPY_FADE_END);
    const messageStart = sp ? MESSAGE_START_SP : MESSAGE_START;
    const messageEnd = sp ? MESSAGE_END_SP : MESSAGE_END;
    const messageOffset = reducedMotion
      ? progress >= 0.5
        ? 0
        : getMessageOffset(1, messageStart, messageEnd)
      : getMessageOffset(progress, messageStart, messageEnd);
    const kvBgFade =
      (BG_OPACITY_MIN +
        (1 - BG_OPACITY_MIN) * (1 - range(progress, 0.15, 0.85))) *
      (1 - getLeadExit(messageOffset)) *
      getKvExitMul();
    const bgFade =
      progress >= 1 ? Math.max(kvBgFade, getNewsBgFade()) : kvBgFade;

    lastCopy = writeOpacity(copyEl, copyFade, lastCopy);
    if (sp) {
      lastBottom = writeOpacity(bottomEl, 1, lastBottom);
      applySpBottomChrome(progress);
    } else {
      lastScrollCue = writeOpacity(scrollCueEl, 1, lastScrollCue);
      lastBottom = writeOpacity(bottomEl, copyFade, lastBottom);
      lastTickerOp = writeOpacity(tickerEl, 1, lastTickerOp);
    }
    writeKvBackground(bgFade);

    if (copyEl) copyEl.style.pointerEvents = copyFade > 0.05 ? "" : "none";
    if (bottomEl && !sp) {
      bottomEl.style.pointerEvents = copyFade > 0.05 ? "" : "none";
    }

    if (messageEl && messageOffset !== lastOffset) {
      messageEl.style.transform = `translate(-50%, calc(-50% + ${messageOffset}px))`;
      lastOffset = messageOffset;
    }

    const travel = range(progress, messageStart, messageEnd);
    const opaqueY = viewH * (0.18 + 0.64 * travel) - 100;
    const fadeSpan = Math.max(viewH * 0.08, (lineHs[0] || 48) * 1.35);
    const fadeStartY = opaqueY + fadeSpan;
    const appearStartY = viewH;
    const idleStill = kvRoot.dataset.kvIdle === "still";

    syncKvHexOpacity({
      progress,
      travel,
      bgFade,
      opaqueY,
      fadeStartY,
      appearStartY,
      uniformOnly: sp || reducedMotion || idleStill || travel >= 1 || travel <= 0,
    });

    if (sp) {
      if (!linesDone) {
        messageLines.forEach((line, i) => {
          lastLineOps[i] = writeOpacity(line, 1, lastLineOps[i]);
        });
        linesDone = true;
      }
      lastMessageOp = writeOpacity(
        messageEl,
        getSpMessageOpacity(progress, travel),
        lastMessageOp,
      );
      return;
    }

    lastMessageOp = writeOpacity(messageEl, 1, lastMessageOp);

    if (reducedMotion || travel >= 1) {
      if (!linesDone) {
        messageLines.forEach((line, i) => {
          lastLineOps[i] = writeOpacity(line, 1, lastLineOps[i]);
        });
        linesDone = true;
      }
      return;
    }

    linesDone = false;
    let lineTop = viewH / 2 - messageH / 2 + messageOffset;

    messageLines.forEach((line, i) => {
      const top = lineTop;
      lineTop += lineHs[i] || 0;
      let opacity = 0;
      if (progress <= 0.02) {
        opacity = 0;
      } else if (top <= opaqueY) {
        opacity = 1;
      } else if (top <= fadeStartY) {
        const t =
          fadeStartY === opaqueY
            ? 1
            : clamp((fadeStartY - top) / (fadeStartY - opaqueY), 0, 1);
        opacity = LINE_OPACITY_MID + (1 - LINE_OPACITY_MID) * t;
      } else if (top < appearStartY) {
        opacity =
          LINE_OPACITY_MID *
          clamp((appearStartY - top) / (appearStartY - fadeStartY), 0, 1);
      }
      lastLineOps[i] = writeOpacity(line, opacity, lastLineOps[i]);
    });
  };

  syncKv = () => {
    if (!kvMotionReady) return;
    updateKv();
  };

  lenis.on("scroll", updateKv);
  // Lenis / ネイティブの着地後に必ず再同期（アンカー飛びで scroll イベントが薄いとき用）
  window.addEventListener(
    "scrollend",
    () => {
      syncKv();
    },
    { passive: true },
  );
  window.addEventListener("inoac:drawer-closed", () => {
    syncKvAfterSettle();
  });
  window.addEventListener("resize", () => {
    syncKvChrome();
    updateKv();
    syncWideHexVisibility(kvRoot);
  }, { passive: true });
  window.visualViewport?.addEventListener("resize", syncKvChrome, {
    passive: true,
  });
  syncKvChrome();
  updateKv();

  initKvLoading(kvRoot, {
    onReady: () => {
      kvMotionReady = true;
      restoreSavedScroll();
      if (kvRoot.dataset.kvOpening === "true" && !skipKvOpening) {
        unlockPageScroll();
      }
      syncHexEls();
      syncKvChrome();
      kvDebugMark("kv-ready");

      const hash = location.hash;
      const hasAnchor =
        hash && hash !== "#" && hash !== "#top" && document.querySelector(hash);
      if (hasAnchor) {
        // 初回 hash / スキップ入場: ブラウザ着地を Lenis に合わせたうえで KV UI を同期
        lenis.scrollTo(hash, {
          immediate: true,
          force: true,
          onComplete: () => syncKvAfterSettle(),
        });
      } else {
        syncKvAfterSettle();
      }
      document.documentElement.classList.remove("is-kv-skip-opening");
    },
  });
} else {
  restoreSavedScroll();
  document.documentElement.classList.remove("is-kv-skip-opening");
}

let productsSplide;
let productsDragging = false;

const productsRoot = document.querySelector(".js-products-splide");
if (productsRoot) {
  const pad = (n) => String(n).padStart(2, "0");
  const fractionEl = document.querySelector(".js-products-fraction");

  const updateFraction = (splide) => {
    if (!fractionEl) return;
    const current = splide.index + 1;
    const total = splide.length;
    fractionEl.innerHTML =
      `<span class="products__fraction-current">${pad(current)}</span>` +
      `<span class="products__fraction-sep" aria-hidden="true"></span>` +
      `<span class="products__fraction-total">${pad(total)}</span>`;
  };

  productsSplide = new Splide(productsRoot, {
    type: "loop",
    focus: "center",
    autoWidth: true,
    gap: "45px",
    autoplay: true,
    interval: 5000,
    pauseOnHover: true,
    arrows: false,
    pagination: false,
    speed: 1400,
    easing: "cubic-bezier(0.22, 0.61, 0.36, 1)",
    noDrag: "button, a, .products__hit",
    dragMinThreshold: { mouse: 12, touch: 16 },
    breakpoints: {
      768: {
        gap: "12px",
      },
    },
  });

  productsSplide.on("mounted move updated", () => updateFraction(productsSplide));
  productsSplide.on("move", () => {
    productsDragging = true;
  });
  productsSplide.on("moved", () => {
    window.setTimeout(() => {
      productsDragging = false;
    }, 50);
  });
  productsSplide.mount();

  document.querySelector(".js-products-prev")?.addEventListener("click", () => {
    productsSplide.go("<");
  });
  document.querySelector(".js-products-next")?.addEventListener("click", () => {
    productsSplide.go(">");
  });
}

const drawer = document.querySelector("#nav-drawer");
const menuBtn = document.querySelector(".js-menu-toggle");
const header = document.querySelector(".header");
let drawerClosing = false;

const normalizePathname = (pathname) => {
  let path = String(pathname || "").replace(/\/index\.html$/i, "");
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return path || "/";
};

const getHomePathname = () => {
  const homeLink = document.querySelector("#nav-drawer .drawer__nav a[href]");
  if (homeLink) {
    return normalizePathname(new URL(homeLink.href, location.href).pathname);
  }
  return normalizePathname(location.pathname);
};

const isSiteHomePath = (pathname) =>
  normalizePathname(pathname) === getHomePathname();

const setMenuExpanded = (expanded) => {
  menuBtn?.setAttribute("aria-expanded", String(expanded));
  menuBtn?.setAttribute("aria-label", expanded ? "メニューを閉じる" : "メニュー");
  header?.classList.toggle("is-menu-open", expanded);
};

const openDrawer = () => {
  if (!drawer || drawerClosing) return;
  drawer.hidden = false;
  setMenuExpanded(true);
  lockPageScroll();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      drawer.classList.add("is-open");
    });
  });
};

const closeDrawer = () => {
  if (!drawer || drawer.hidden || drawerClosing) return;
  drawerClosing = true;
  drawer.classList.remove("is-open");
  setMenuExpanded(false);

  const panel = drawer.querySelector(".drawer__panel");
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    panel?.removeEventListener("transitionend", onEnd);
    window.clearTimeout(fallbackTimer);
    drawer.hidden = true;
    unlockPageScroll();
    drawerClosing = false;
    window.dispatchEvent(new Event("inoac:drawer-closed"));
  };

  const onEnd = (event) => {
    if (event.target !== panel || event.propertyName !== "transform") return;
    finish();
  };

  if (!panel || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    finish();
    return;
  }

  panel.addEventListener("transitionend", onEnd);
  const fallbackTimer = window.setTimeout(finish, 520);
};

const whenDrawerClosed = (fn) => {
  if (!drawer || drawer.hidden) {
    fn();
    return;
  }
  if (!drawer.classList.contains("is-open") && !drawerClosing) {
    fn();
    return;
  }
  window.addEventListener("inoac:drawer-closed", fn, { once: true });
  if (!drawerClosing) closeDrawer();
};

const scrollPageToTarget = (target, { offset = -24 } = {}) => {
  lenis.resize();
  lenis.reset();
  if (target === 0) {
    lenis.scrollTo(0, {
      force: true,
      onComplete: () => syncKvAfterSettle(),
    });
    return;
  }
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!(el instanceof Element)) {
    syncKvAfterSettle();
    return;
  }
  const y = Math.min(
    lenis.limit,
    Math.max(0, el.getBoundingClientRect().top + window.scrollY + offset),
  );
  lenis.scrollTo(y, {
    force: true,
    onComplete: () => syncKvAfterSettle(),
  });
};

const toggleDrawer = () => {
  if (!drawer || drawerClosing) return;
  if (drawer.hidden || !drawer.classList.contains("is-open")) {
    openDrawer();
  } else {
    closeDrawer();
  }
};

menuBtn?.addEventListener("pointerdown", () => {
  menuBtn.classList.add("is-menu-press");
});
menuBtn?.addEventListener("pointerleave", () => {
  menuBtn.classList.remove("is-menu-press");
});
menuBtn?.addEventListener("click", () => {
  menuBtn.classList.add("is-menu-press");
  toggleDrawer();
});
drawer?.querySelectorAll(".js-menu-close").forEach((btn) => {
  btn.addEventListener("click", closeDrawer);
});

// ドロワー内アンカー: History ナビ同様に capture で閉じる→ Lenis 移動→ syncKv
drawer?.addEventListener(
  "click",
  (event) => {
    const link = event.target.closest("a[href]");
    if (!link || !drawer.contains(link)) return;

    let url;
    try {
      url = new URL(link.href, location.href);
    } catch {
      closeDrawer();
      return;
    }

    const sameOrigin = url.origin === location.origin;
    const samePath =
      normalizePathname(url.pathname) === normalizePathname(location.pathname);
    const onTop = document.body.classList.contains("page--top");
    const hash = url.hash || "";

    // 同一トップ内の Home / #products / #news 等
    if (sameOrigin && samePath && onTop) {
      const isHomeHash = !hash || hash === "#" || hash === "#top";
      const isSectionHash =
        hash === "#products" || hash === "#news" || hash === "#history";
      if (isHomeHash || isSectionHash) {
        event.preventDefault();
        event.stopImmediatePropagation();
        whenDrawerClosed(() => {
          if (isHomeHash) {
            if (location.hash) {
              history.pushState(null, "", location.pathname + location.search);
            }
            scrollPageToTarget(0);
            return;
          }
          if (location.hash !== hash) {
            history.pushState(null, "", hash);
          }
          scrollPageToTarget(hash);
        });
        return;
      }
    }

    // 他ページへ（History 等）: 閉じるだけ。遷移は page transition 側
    closeDrawer();
  },
  true,
);
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  if (drawer && !drawer.hidden) {
    closeDrawer();
    return;
  }
  document.querySelectorAll(".modal:not([hidden])").forEach((modal) => {
    closeModal(modal);
  });
});

const modalReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const fillProductModal = (trigger) => {
  const modal = document.getElementById("modal-product");
  if (!modal) return;

  const yearEl = modal.querySelector(".js-product-modal-year");
  const titleEl = modal.querySelector(".js-product-modal-title");
  const textEl = modal.querySelector(".js-product-modal-text");
  const badgeEl = modal.querySelector(".js-product-modal-badge");
  const mediaEl = modal.querySelector(".js-product-modal-media");
  const year = trigger.dataset.year || "";
  const badge = trigger.dataset.badge || "";
  const images = (trigger.dataset.images || "")
    .split(",")
    .map((src) => src.trim())
    .filter(Boolean);

  if (yearEl) {
    yearEl.textContent = year;
    yearEl.hidden = !year;
  }
  if (titleEl) titleEl.textContent = trigger.dataset.title || "";
  if (textEl) textEl.textContent = trigger.dataset.text || "";
  if (badgeEl) {
    badgeEl.hidden = !badge;
    const leadEl = badgeEl.querySelector(".js-badge-lead");
    const tailEl = badgeEl.querySelector(".js-badge-tail");
    if (badge && leadEl && tailEl) {
      leadEl.textContent = badge.charAt(0);
      tailEl.textContent = badge.slice(1);
    }
  }
  if (mediaEl) {
    mediaEl.replaceChildren(
      ...images.map((src) => {
        const img = document.createElement("img");
        img.src = src;
        img.alt = "";
        return img;
      }),
    );
    mediaEl.classList.toggle("is-single", images.length < 2);
  }
};

const syncModalViewport = () => {
  const h = window.visualViewport?.height ?? window.innerHeight;
  document.documentElement.style.setProperty("--modal-vh", `${h}px`);
};

const syncModalViewportIfOpen = () => {
  if (!document.querySelector(".modal.is-open, .modal:not([hidden])")) return;
  syncModalViewport();
};

window.visualViewport?.addEventListener("resize", syncModalViewportIfOpen, {
  passive: true,
});
window.visualViewport?.addEventListener("scroll", syncModalViewportIfOpen, {
  passive: true,
});

const openModal = (modal) => {
  modal.hidden = false;
  syncModalViewport();
  lockPageScroll();
  const scroller = modal.querySelector(".modal__dialog");
  if (scroller) scroller.scrollTop = 0;

  if (modal.id === "modal-product") {
    productsSplide?.Components?.Autoplay?.pause();
  }

  if (modalReducedMotion) {
    modal.classList.add("is-open");
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add("is-open");
    });
  });
};

const closeModal = (modal) => {
  if (!modal || modal.hidden) return;

  if (modalReducedMotion || !modal.classList.contains("is-open")) {
    finishCloseModal(modal);
    return;
  }

  const onEnd = (event) => {
    if (event.target !== modal.querySelector(".modal__overlay")) return;
    if (event.propertyName !== "opacity") return;
    modal.removeEventListener("transitionend", onEnd);
    finishCloseModal(modal);
  };

  modal.addEventListener("transitionend", onEnd);
  modal.classList.remove("is-open");
  setTimeout(() => {
    modal.removeEventListener("transitionend", onEnd);
    finishCloseModal(modal);
  }, 400);
};

const finishCloseModal = (modal) => {
  if (!modal || modal.hidden) return;
  modal.classList.remove("is-open");
  modal.setAttribute("hidden", "");
  unlockPageScroll();
  if (modal.id === "modal-product") {
    productsSplide?.Components?.Autoplay?.play();
  }
};

document.addEventListener("click", (event) => {
  const closer = event.target.closest(".js-modal-close");
  if (closer) {
    closeModal(closer.closest(".modal"));
    return;
  }

  const opener = event.target.closest(".js-modal-open");
  if (!opener) return;
  if (opener.classList.contains("js-product-modal") && productsDragging) {
    event.preventDefault();
    return;
  }
  const modal = document.getElementById(opener.dataset.modal);
  if (!modal) return;
  if (opener.classList.contains("js-product-modal")) fillProductModal(opener);
  openModal(modal);
});

// capture で Lenis anchors (#top) より先に処理し、状態ずれ時も毎回同じ ease で最上部へ
document.addEventListener(
  "click",
  (event) => {
    const link = event.target.closest(".js-pagetop");
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    lenis.reset();
    const distance = lenis.scroll;
    if (distance <= 24) {
      // ほぼ最上部でも KV 表示だけ同期（opacity ずれの保険）
      syncKvAfterSettle();
      return;
    }
    const duration = Math.min(2.6, Math.max(1.2, distance / 900));
    lenis.scrollTo(0, {
      duration,
      easing: easeOutPagetop,
      lerp: null,
      force: true,
      onComplete: () => syncKvAfterSettle(),
    });
  },
  true,
);

const pageTransitionReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;
let pageTransitioning = false;

const shouldAnimatePageLink = (link, event) => {
  if (
    !link ||
    pageTransitionReducedMotion ||
    pageTransitioning ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  if (link.target && link.target !== "_self") return false;
  if (link.hasAttribute("download")) return false;

  const href = link.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return false;
  }

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  // 同一パスの hash / query だけならページ遷移しない（ドロワーの #products 等）
  if (normalizePathname(url.pathname) === normalizePathname(window.location.pathname)) {
    return false;
  }

  return true;
};

document.addEventListener(
  "click",
  (event) => {
    const link = event.target.closest("a[href]");
    if (!shouldAnimatePageLink(link, event)) return;
    pageTransitioning = true;
    event.preventDefault();

    // M2: 他ページ → トップは skip opening + 先頭から
    try {
      const url = new URL(link.href, location.href);
      if (isSiteHomePath(url.pathname) && (!url.hash || url.hash === "#" || url.hash === "#top")) {
        markKvIntroPlayed();
        sessionStorage.setItem(`inoac:scroll:${url.pathname}`, "0");
        const withSlash = url.pathname.endsWith("/")
          ? url.pathname.slice(0, -1)
          : `${url.pathname}/`;
        if (withSlash !== url.pathname) {
          sessionStorage.setItem(`inoac:scroll:${withSlash}`, "0");
        }
      }
    } catch {
      /* ignore */
    }

    persistScrollPosition();
    document.body.classList.add("is-page-transitioning");
    window.setTimeout(() => {
      window.location.href = link.href;
    }, 380);
  },
  true,
);

const historyTimeline = document.querySelector(".history-timeline");
const historyMain = document.querySelector(".history-main");

const buildHistoryTimelineSegments = () => {
  if (!historyTimeline || !historyMain) return;

  const isSp = window.matchMedia("(max-width: 768px)").matches;
  const dotPeriod = isSp ? 10 : 14;
  const dotSize = isSp ? 4 : 6;
  const bgPosX = isSp ? 10 : 15;
  const minGap =
    parseFloat(
      getComputedStyle(document.querySelector(".page--history") || document.body)
        .getPropertyValue("--history-topic-gap"),
    ) || 4;

  const dots = [...document.querySelectorAll(".history-topic-dot")];
  dots.forEach((dot) => dot.style.setProperty("--topic-dot-snap-y", "0px"));

  const railTop = historyTimeline.getBoundingClientRect().top + window.scrollY;
  const railHeight = historyMain.offsetHeight;

  const ringHeightFor = (dot) =>
    dot.classList.contains("history-topic-dot--lg")
      ? isSp
        ? 24
        : 36
      : isSp
        ? 16
        : 24;

  // dotSize + ringHeight + 2G ≡ 0 (mod dotPeriod) のとき上下余白が同一ピクセルになる
  const symmetricGapFor = (ringHeight) => {
    for (let gap = minGap; gap < minGap + dotPeriod; gap += 1) {
      if ((dotSize + ringHeight + 2 * gap) % dotPeriod === 0) return gap;
    }
    return minGap;
  };

  const pickSymmetricSnap = (desiredTop, ringHeight) => {
    const gap = symmetricGapFor(ringHeight);
    const centerN = Math.round((desiredTop - dotSize - gap) / dotPeriod);

    let bestTop = centerN * dotPeriod + dotSize + gap;
    let bestDist = Math.abs(bestTop - desiredTop);

    for (let n = centerN - 2; n <= centerN + 2; n += 1) {
      const top = n * dotPeriod + dotSize + gap;
      const dist = Math.abs(top - desiredTop);
      if (dist < bestDist) {
        bestDist = dist;
        bestTop = top;
      }
    }

    return { top: bestTop, gap };
  };

  const markers = dots
    .map((dot) => {
      const ringHeight = ringHeightFor(dot);
      const rect = dot.getBoundingClientRect();
      const desiredTop = rect.top + window.scrollY - railTop;
      const { top: snapTop, gap } = pickSymmetricSnap(desiredTop, ringHeight);
      const delta = Math.round(snapTop - desiredTop);

      dot.style.setProperty("--topic-dot-snap-y", `${delta}px`);

      return {
        top: snapTop,
        bottom: snapTop + ringHeight,
        gap,
      };
    })
    .sort((a, b) => a.top - b.top);

  historyTimeline.replaceChildren();

  const addSegment = (rangeStart, rangeEnd) => {
    const firstIndex = Math.ceil(rangeStart / dotPeriod);
    const lastIndex = Math.floor((rangeEnd - dotSize) / dotPeriod);
    if (lastIndex < firstIndex) return;

    const startY = firstIndex * dotPeriod;
    const height = (lastIndex - firstIndex + 1) * dotPeriod;
    if (height < dotPeriod) return;

    const seg = document.createElement("div");
    seg.className = "history-timeline__segment";
    seg.style.top = `${startY}px`;
    seg.style.height = `${height}px`;
    seg.style.backgroundPosition = `${bgPosX}px 0`;
    historyTimeline.appendChild(seg);
  };

  let cursor = 0;
  markers.forEach((marker) => {
    addSegment(cursor, marker.top - marker.gap);
    cursor = marker.bottom + marker.gap;
  });
  addSegment(cursor, railHeight);
};

if (historyTimeline && historyMain) {
  buildHistoryTimelineSegments();
  window.addEventListener("resize", buildHistoryTimelineSegments);
  window.addEventListener("load", buildHistoryTimelineSegments);
  if (document.fonts?.ready) {
    document.fonts.ready.then(buildHistoryTimelineSegments);
  }
  if (typeof ResizeObserver !== "undefined") {
    const timelineObserver = new ResizeObserver(buildHistoryTimelineSegments);
    timelineObserver.observe(historyMain);
  }
}

const historyNav = document.querySelector(".js-history-nav");
const historyDecades = [...document.querySelectorAll(".js-history-decade")];
if (historyNav && historyDecades.length) {
  const historyLinks = [...historyNav.querySelectorAll("[data-decade]")];

  const setActiveDecade = (id) => {
    historyLinks.forEach((link) => {
      link.classList.toggle("is-active", link.dataset.decade === id);
    });
  };

  // ナビクリック後の Lenis 移動中は IO が通過中の年代で上書きしない
  let navScrollTargetId = null;

  // capture で Lenis anchors より先に止め、画像読み込みによる着地ずれは先頭以外だけ再補正
  historyNav.addEventListener(
    "click",
    (event) => {
      const link = event.target.closest("[data-decade]");
      if (!link) return;
      const target = document.getElementById(link.dataset.decade);
      if (!target) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navScrollTargetId = link.dataset.decade;
      setActiveDecade(navScrollTargetId);
      target.classList.add("is-visible");
      const isSp = window.matchMedia("(max-width: 768px)").matches;
      // SP の 1920s だけ先頭パディングがなく、他年代より見出しが上に着地する
      const offset = isSp
        ? link.dataset.decade === "1920s"
          ? -176
          : -96
        : -24;

      // animatedScroll が実スクロールより大きいと fromTo の開始が一瞬 limit（最下部）になる
      // 寸法更新 → 内部位置を実スクロールに同期 → window.scrollY 基準の絶対座標で飛ばす
      lenis.resize();
      lenis.reset();
      const y = Math.min(
        lenis.limit,
        Math.max(
          0,
          target.getBoundingClientRect().top + window.scrollY + offset,
        ),
      );
      lenis.scrollTo(y, {
        force: true,
        onComplete: () => {
          setActiveDecade(navScrollTargetId);
          // 1920s などページ先頭クランプ中は触らない
          if (lenis.scroll < 48) {
            navScrollTargetId = null;
            return;
          }
          const expectedTop = -offset;
          const top = target.getBoundingClientRect().top;
          // スクロール中のレイアウト伸びで着地がずれたときだけ合わせる
          if (Math.abs(top - expectedTop) > 16) {
            lenis.resize();
            const y2 = Math.min(
              lenis.limit,
              Math.max(
                0,
                target.getBoundingClientRect().top + window.scrollY + offset,
              ),
            );
            // 再補正中も IO にアクティブを奪われないよう target を保持
            lenis.scrollTo(y2, {
              immediate: true,
              force: true,
              onComplete: () => {
                setActiveDecade(navScrollTargetId);
                navScrollTargetId = null;
              },
            });
            return;
          }
          navScrollTargetId = null;
        },
      });
    },
    true,
  );

  if (typeof IntersectionObserver !== "undefined") {
    const ratios = new Map();
    const historyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.set(entry.target.id, entry.intersectionRatio);
        });
        if (navScrollTargetId) {
          setActiveDecade(navScrollTargetId);
          return;
        }
        let bestId = historyDecades[0]?.id;
        let bestRatio = 0;
        ratios.forEach((ratio, id) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestId) setActiveDecade(bestId);
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );
    historyDecades.forEach((section) => historyObserver.observe(section));
  }

  const siteFooter = document.querySelector(".footer");
  if (siteFooter && typeof IntersectionObserver !== "undefined") {
    const footerObserver = new IntersectionObserver(
      ([entry]) => {
        historyNav.classList.toggle("is-faded", entry.isIntersecting);
      },
      { threshold: 0.02 },
    );
    footerObserver.observe(siteFooter);
  }
}

const initReveal = (els, options) => {
  if (!els.length) return;

  const revealNow = () => {
    els.forEach((el) => el.classList.add("is-visible"));
  };

  if (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    typeof IntersectionObserver === "undefined"
  ) {
    revealNow();
    return;
  }

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      revealObserver.unobserve(entry.target);
    });
  }, options);

  els.forEach((el) => revealObserver.observe(el));
};

const isSpReveal = window.matchMedia("(max-width: 768px)").matches;
initReveal([...document.querySelectorAll(".js-reveal:not(.history-decade)")], isSpReveal
  ? { rootMargin: "0px 0px -12% 0px", threshold: 0.02 }
  : { rootMargin: "0px 0px -36% 0px", threshold: 0.08 });
// 年代ブロックは縦に長いので、先頭（見出し付近）が画面中ほどまで来てから発火する
initReveal([...document.querySelectorAll(".history-decade.js-reveal")], isSpReveal
  ? { rootMargin: "0px 0px -21% 0px", threshold: 0 }
  : { rootMargin: "0px 0px -35% 0px", threshold: 0 });
initReveal([...document.querySelectorAll(".js-chara-reveal")], isSpReveal
  ? { rootMargin: "0px 0px -6% 0px", threshold: 0.01 }
  : { rootMargin: "0px 0px -10% 0px", threshold: 0.01 });
